/**
 * Universal Ontology Registry
 *
 * A runtime IS-A hierarchy that all domains contribute to. The TypeScript
 * union types in `graph/types.ts` enforce "valid type strings" — this
 * registry adds the inheritance relationships.
 *
 * Domains register concrete types as descendants of abstract types. Cross-
 * domain code (Compass rules, AI tools, view profiles) queries the hierarchy
 * via `isA()` to write provider-agnostic logic.
 *
 * Example: `isA('mongodb', 'database')` → true
 *          `isA('aws_documentdb_instance', 'document_database')` → true
 *          `isA('person', 'datastore')` → false
 */

// ─── Abstract type hierarchy (the universal layer) ─────────────

const ABSTRACT_PARENTS: Record<string, string | null> = {
  // Root
  thing: null,

  // Top-level abstractions (everything inherits from one of these)
  agent: 'thing',
  place: 'thing',
  artifact: 'thing',
  datastore: 'thing',
  policy: 'thing',
  event: 'thing',
  concept: 'thing',
  group: 'thing',

  // ─── Agents (things with agency) ─────────────
  person: 'agent',
  organization: 'agent',
  service_identity: 'agent',
  user_identity: 'agent',
  identity_group: 'agent',
  identity_provider: 'agent',

  // ─── Places (locations, boundaries, containers) ─────────────
  geographic_region: 'place',
  availability_zone: 'place',
  account: 'place',
  network_boundary: 'place',
  subnet: 'network_boundary',
  lane: 'place',
  pool: 'place',

  // ─── Artifacts (created/maintained things) ─────────────
  compute: 'artifact',
  server: 'compute',
  function_service: 'compute',
  container_instance: 'compute',
  container_orchestrator: 'compute',
  application: 'compute',
  managed_compute: 'compute',

  network_path: 'artifact',
  load_balancer: 'network_path',
  api_gateway: 'network_path',
  cdn: 'network_path',
  dns_zone: 'network_path',
  network_edge: 'network_path',
  service_mesh: 'network_path',

  messaging_artifact: 'artifact',
  message_queue: 'messaging_artifact',
  message_topic: 'messaging_artifact',
  event_stream: 'messaging_artifact',
  event_bus: 'messaging_artifact',

  observability_artifact: 'artifact',
  log_store: 'observability_artifact',
  metric_store: 'observability_artifact',
  audit_log: 'observability_artifact',
  trace_store: 'observability_artifact',

  // ─── Datastores (anything that persists data) ─────────────
  database: 'datastore',
  relational_database: 'database',
  document_database: 'database',
  key_value_store: 'database',
  graph_database: 'database',
  wide_column_store: 'database',
  time_series_database: 'database',
  vector_database: 'database',
  search_index: 'database',
  data_warehouse: 'database',

  object_storage: 'datastore',
  file_storage: 'datastore',
  block_storage: 'datastore',

  // ─── Policies (rules that enforce something) ─────────────
  network_acl: 'policy',
  access_policy: 'policy',
  encryption_key: 'policy',
  certificate: 'policy',
  secret_store: 'policy',
  web_app_firewall: 'policy',
  threat_detector: 'policy',

  // GRC types map into the policy/event abstractions
  control: 'policy',
  obligation: 'policy',
  risk: 'event',
  finding: 'event',
  evidence: 'artifact',

  // ─── Concepts (abstract ideas — for mindmaps, knowledge maps) ─────────────
  topic: 'concept',
  question: 'concept',
  idea: 'concept',
};

// ─── Runtime registry ─────────────

const HIERARCHY = new Map<string, string | null>(Object.entries(ABSTRACT_PARENTS));
const CHILDREN_OF = new Map<string, Set<string>>();

// Build initial children index
for (const [child, parent] of HIERARCHY.entries()) {
  if (parent) {
    if (!CHILDREN_OF.has(parent)) CHILDREN_OF.set(parent, new Set());
    CHILDREN_OF.get(parent)!.add(child);
  }
}

/**
 * Register a concrete type as a descendant of an existing type.
 * Domains call this at startup to add their semantic types to the hierarchy.
 *
 * @example
 *   registerOntologyType('postgres', 'relational_database');
 *   registerOntologyType('mongodb', 'document_database');
 *   registerOntologyType('aurora_postgres', 'postgres');
 */
export function registerOntologyType(type: string, parent: string): void {
  if (!HIERARCHY.has(parent)) {
    throw new Error(`Cannot register '${type}' under unknown parent '${parent}'`);
  }
  HIERARCHY.set(type, parent);
  if (!CHILDREN_OF.has(parent)) CHILDREN_OF.set(parent, new Set());
  CHILDREN_OF.get(parent)!.add(type);
}

/**
 * Register many types at once.
 *
 * @example
 *   registerOntologyTypes({
 *     postgres: 'relational_database',
 *     mysql: 'relational_database',
 *     mongodb: 'document_database',
 *   });
 */
export function registerOntologyTypes(map: Record<string, string>): void {
  for (const [type, parent] of Object.entries(map)) {
    registerOntologyType(type, parent);
  }
}

/**
 * Check if `type` is a descendant of (or equal to) `ancestor`.
 *
 * @example
 *   isA('postgres', 'relational_database') // true
 *   isA('postgres', 'database')            // true
 *   isA('postgres', 'datastore')           // true
 *   isA('postgres', 'thing')               // true
 *   isA('postgres', 'agent')               // false
 */
export function isA(type: string, ancestor: string): boolean {
  if (type === ancestor) return true;
  let current: string | null | undefined = HIERARCHY.get(type);
  while (current) {
    if (current === ancestor) return true;
    current = HIERARCHY.get(current);
  }
  return false;
}

/**
 * Get the full ancestor chain for a type, from immediate parent to root.
 *
 * @example
 *   getAncestors('postgres')
 *   // ['relational_database', 'database', 'datastore', 'thing']
 */
export function getAncestors(type: string): string[] {
  const ancestors: string[] = [];
  let current = HIERARCHY.get(type);
  while (current) {
    ancestors.push(current);
    current = HIERARCHY.get(current);
  }
  return ancestors;
}

/**
 * Get all known descendant types of an abstract type.
 *
 * @example
 *   getDescendants('database')
 *   // ['relational_database', 'document_database', 'postgres', 'mongodb', ...]
 */
export function getDescendants(type: string): string[] {
  const result: string[] = [];
  const queue = [type];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const children = CHILDREN_OF.get(current);
    if (children) {
      for (const child of children) {
        result.push(child);
        queue.push(child);
      }
    }
  }
  return result;
}

/**
 * Get the immediate parent of a type, or null if it's a root.
 */
export function getParent(type: string): string | null {
  return HIERARCHY.get(type) ?? null;
}

/**
 * Check if a type is registered in the ontology.
 */
export function hasType(type: string): boolean {
  return HIERARCHY.has(type);
}

/**
 * List all registered types (useful for debugging and tooling).
 */
export function getAllTypes(): string[] {
  return Array.from(HIERARCHY.keys());
}

/**
 * Find the most specific common ancestor of two types.
 * Useful for "what's the closest thing these have in common?" queries.
 *
 * @example
 *   commonAncestor('postgres', 'mongodb')  // 'database'
 *   commonAncestor('postgres', 's3')       // 'datastore'
 *   commonAncestor('postgres', 'person')   // 'thing'
 */
export function commonAncestor(typeA: string, typeB: string): string | null {
  const ancestorsA = new Set([typeA, ...getAncestors(typeA)]);
  if (ancestorsA.has(typeB)) return typeB;
  let current: string | null | undefined = HIERARCHY.get(typeB);
  while (current) {
    if (ancestorsA.has(current)) return current;
    current = HIERARCHY.get(current);
  }
  return null;
}
